/**
 * CollectionDetailPage
 *
 * Page displaying a single collection with products and filters.
 * Layout matches Shapermint's collection page structure:
 * - Desktop: sidebar filter panel + sort dropdown + product grid
 * - Mobile: "Filter By" button + drawer from right + sort dropdown row
 * - Append-based "Load More" pagination with progress bar
 * - All filter/sort state synced to URL query params
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCollectionPage } from '../lib/hooks/use-collection-page';
import { PackCollectionGrid } from '../components/organisms/PackCollectionGrid';
import { ProductGrid } from '../components/organisms/ProductGrid';
import { ProductFilters } from '../components/organisms/ProductFilters';
import { CategoryNav } from '../components/organisms/CategoryNav';
import { TopBanner } from '../components/organisms/TopBanner';
import { SearchFilters, CollectionProduct } from '../domain/entities/collection.entity';
import { useIntl } from '@/lib/intl';
import { FunnelExperience, FunnelErrorBoundary } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '@/lib/hooks/use-funnel-add-to-cart';
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerCloseButton,
  DrawerBody,
  DrawerFooter,
  DrawerPrimaryButton,
  DrawerOutlineButton,
  Breadcrumb,
  ErrorState,
  SortRow,
  SortContainer,
  SortLabel,
  SortSelect,
  LoadMoreContainer,
  LoadMoreButton,
  ProgressContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressText,
  FullWidth,
  PageContainer,
  MainContent,
  Sidebar,
  ProductsSection,
  MobileControlsRow,
  FilterByButton,
  FilterBadge,
} from '@template-project/afrodita-components';

/* ---------- helpers ------------------------------------------------------- */

function parseInitialFilters(searchParams: URLSearchParams): SearchFilters {
  const options: Record<string, string> = {};
  const optionPattern = /^option\[(.+)]$/;
  for (const [key, value] of searchParams.entries()) {
    const match = optionPattern.exec(key);
    if (match) {
      options[match[1]] = value;
    }
  }

  return {
    q: searchParams.get('q') || undefined,
    page: Number.parseInt(searchParams.get('page') || '1', 10),
    limit: 20,
    sort: searchParams.get('sort') || undefined,
    tags: searchParams.getAll('tags'),
    priceMin: searchParams.get('price_min')
      ? Number.parseFloat(searchParams.get('price_min')!)
      : undefined,
    priceMax: searchParams.get('price_max')
      ? Number.parseFloat(searchParams.get('price_max')!)
      : undefined,
    options: Object.keys(options).length > 0 ? options : undefined,
  };
}

function filtersToSearchParams(f: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (f.q) params.set('q', f.q);
  if (f.sort) params.set('sort', f.sort);
  if (f.tags) f.tags.forEach((t) => params.append('tags', t));
  if (f.priceMin != null) params.set('price_min', f.priceMin.toString());
  if (f.priceMax != null) params.set('price_max', f.priceMax.toString());
  if (f.options) {
    for (const [key, value] of Object.entries(f.options)) {
      params.set(`option[${key}]`, value);
    }
  }
  return params;
}

function countActiveFilters(f: SearchFilters): number {
  let count = 0;
  if (f.priceMin != null || f.priceMax != null) count += 1;
  if (f.options) count += Object.keys(f.options).length;
  return count;
}

/* ---------- sort options component --------------------------------------- */

function SortDropdown({
  value,
  onChange,
}: Readonly<{
  value: string;
  onChange: (val: string | undefined) => void;
}>) {
  return (
    <SortContainer>
      <SortLabel>Sort By</SortLabel>
      <SortSelect
        value={value}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">Best Selling</option>
        <option value="price_asc">Price: Low-High</option>
        <option value="price_desc">Price: High-Low</option>
        <option value="newest">New Arrivals</option>
      </SortSelect>
    </SortContainer>
  );
}

/* ---------- component ---------------------------------------------------- */

export function CollectionDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParameters = Object.fromEntries(searchParams.entries());
  const { localizedPath, countryConfig } = useIntl();
  const handleFunnelAddToCart = useFunnelAddToCart();

  const [filters, setFilters] = useState<SearchFilters>(() =>
    parseInitialFilters(searchParams),
  );
  const [accumulatedProducts, setAccumulatedProducts] = useState<CollectionProduct[]>([]);
  const [currentPage, setCurrentPage] = useState(filters.page || 1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState<SearchFilters>(filters);
  const isLoadMoreRef = useRef(false);

  const { data, isLoading: loading, error: rawError } = useCollectionPage(slug || '', filters);
  let error: string | null = null;
  if (rawError instanceof Error) {
    error = rawError.message;
  } else if (rawError) {
    error = String(rawError);
  }

  useEffect(() => {
    if (!data?.products) return;

    if (isLoadMoreRef.current) {
      setAccumulatedProducts((prev) => [...prev, ...data.products]);
      isLoadMoreRef.current = false;
    } else {
      setAccumulatedProducts(data.products);
    }
  }, [data]);

  const syncUrlParams = useCallback(
    (f: SearchFilters) => {
      setSearchParams(filtersToSearchParams(f), { replace: true });
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (newFilters: SearchFilters) => {
      const updated = { ...newFilters, page: 1 };
      setFilters(updated);
      setCurrentPage(1);
      isLoadMoreRef.current = false;
      syncUrlParams(updated);
    },
    [syncUrlParams],
  );

  const handleSortChange = useCallback(
    (sortValue: string | undefined) => {
      handleFilterChange({ ...filters, sort: sortValue, page: 1 });
    },
    [filters, handleFilterChange],
  );

  const handleLoadMore = useCallback(() => {
    if (!data?.pagination || loading) return;
    const nextPage = currentPage + 1;
    if (nextPage > data.pagination.totalPages) return;

    isLoadMoreRef.current = true;
    setCurrentPage(nextPage);
    setFilters((prev) => ({ ...prev, page: nextPage }));
  }, [data, loading, currentPage]);

  const openDrawer = useCallback(() => {
    setMobileFilters(filters);
    setDrawerOpen(true);
  }, [filters]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const applyMobileFilters = useCallback(() => {
    handleFilterChange(mobileFilters);
    setDrawerOpen(false);
  }, [mobileFilters, handleFilterChange]);

  const clearMobileFilters = useCallback(() => {
    const cleared: SearchFilters = { page: 1, limit: filters.limit };
    setMobileFilters(cleared);
    handleFilterChange(cleared);
    setDrawerOpen(false);
  }, [filters.limit, handleFilterChange]);

  if (error) {
    return (
      <PageContainer>
        <ErrorState>
          <p>Failed to load collection: {error}</p>
        </ErrorState>
      </PageContainer>
    );
  }

  const collection = data?.collection;
  const isPackCollection = collection?.type === 'pack';
  const packs = data?.packs ?? [];
  const facets = data?.facets || {};
  const pagination = data?.pagination;

  const totalProducts = pagination?.total || accumulatedProducts.length;
  const viewedProducts = Math.min(accumulatedProducts.length, totalProducts);
  const progressPercent =
    totalProducts > 0 ? (viewedProducts / totalProducts) * 100 : 0;
  const totalPages = pagination?.totalPages || 1;
  const isLastPage = currentPage >= totalPages;

  const activeFilterCount = countActiveFilters(filters);

  return (
    <FullWidth>
      <TopBanner title={collection?.title} description={collection?.description} />

      <PageContainer>
        <CategoryNav currentSlug={slug} />

        <Breadcrumb>
          <a href={localizedPath('/')}>Home</a>
          <span className="separator">|</span>
          <span className="current">{collection?.title || '...'}</span>
        </Breadcrumb>

        <FunnelErrorBoundary>
          <FunnelExperience
            pageType="collection"
            context={{
              collectionId: slug,
              url: `/collections/${slug ?? ''}`,
              country: countryConfig.countryCode,
              urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
            }}
            onAddToCart={handleFunnelAddToCart}
          />
        </FunnelErrorBoundary>

        {!isPackCollection && (
          <>
            {/* Mobile: Filter By + Sort row */}
            <MobileControlsRow>
              <FilterByButton onClick={openDrawer}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="8" y1="12" x2="20" y2="12" />
                  <line x1="12" y1="18" x2="20" y2="18" />
                </svg>
                Filter By
                {activeFilterCount > 0 && <FilterBadge>{activeFilterCount}</FilterBadge>}
              </FilterByButton>
              <SortDropdown value={filters.sort || ''} onChange={handleSortChange} />
            </MobileControlsRow>

            {/* Mobile filter drawer */}
            <Drawer isOpen={drawerOpen} onClose={closeDrawer} width="400px">
          <DrawerHeader>
            <DrawerTitle>Filter By</DrawerTitle>
            <DrawerCloseButton onClick={closeDrawer} aria-label="Close filters">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </DrawerCloseButton>
          </DrawerHeader>
          <DrawerBody>
            <ProductFilters
              facets={facets}
              filters={mobileFilters}
              onFilterChange={setMobileFilters}
            />
          </DrawerBody>
          <DrawerFooter>
            <DrawerOutlineButton onClick={clearMobileFilters}>Clear All</DrawerOutlineButton>
            <DrawerPrimaryButton onClick={applyMobileFilters}>Apply</DrawerPrimaryButton>
          </DrawerFooter>
            </Drawer>
          </>
        )}

        <MainContent>
          {!isPackCollection && (
            <Sidebar>
              <ProductFilters
                facets={facets}
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </Sidebar>
          )}

          <ProductsSection>
            {!isPackCollection && (
              <SortRow>
                <SortDropdown value={filters.sort || ''} onChange={handleSortChange} />
              </SortRow>
            )}

            {isPackCollection ? (
              <PackCollectionGrid packs={packs} loading={loading} />
            ) : (
              <ProductGrid products={accumulatedProducts} loading={loading} />
            )}

            {!isPackCollection && pagination && totalPages > 1 && (
              <LoadMoreContainer>
                {!isLastPage && (
                  <LoadMoreButton
                    $disabled={loading}
                    disabled={loading}
                    onClick={handleLoadMore}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </LoadMoreButton>
                )}
                <ProgressContainer>
                  <ProgressBarTrack>
                    <ProgressBarFill $percent={progressPercent} />
                  </ProgressBarTrack>
                  <ProgressText>
                    You&apos;ve viewed {viewedProducts} of {totalProducts} products
                  </ProgressText>
                </ProgressContainer>
              </LoadMoreContainer>
            )}
          </ProductsSection>
        </MainContent>
      </PageContainer>
    </FullWidth>
  );
}

export default CollectionDetailPage;
